"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type NutritionDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * A bright, morning kitchen: sage subway tiles, a window throwing daylight
 * across an oak counter, a shelf of jars and basil, and on the counter a green
 * smoothie, a loaded grain bowl (the focal point) and a board of avocado and
 * lemon. The cabinet front under the bottom-centre participant pill is plain.
 *
 * Static art, apart from one CSS opacity loop on the window light (off under
 * reduced motion).
 */
const NUTRITION_KEYFRAMES = `
@keyframes nutrition-light { 0%, 100% { opacity: 0.7 } 50% { opacity: 1 } }
.nutrition-light { animation: nutrition-light 7s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .nutrition-light { animation: none }
}
`;

/** Back edge of the counter top, in px from the top of the scene. */
const COUNTER = 132;

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `nutrition-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <pattern
          id={id("tiles")}
          width="24"
          height="12"
          patternUnits="userSpaceOnUse"
        >
          <rect width="24" height="12" fill="#a9c7ad" />
          <rect
            x="0.6"
            y="0.6"
            width="22.8"
            height="4.8"
            rx="1"
            fill="#b9d6bc"
          />
          <rect
            x="-11.4"
            y="6.6"
            width="22.8"
            height="4.8"
            rx="1"
            fill="#b4d1b7"
          />
          <rect
            x="12.6"
            y="6.6"
            width="22.8"
            height="4.8"
            rx="1"
            fill="#b4d1b7"
          />
          <rect
            x="1.4"
            y="1.2"
            width="20"
            height="0.8"
            fill="#fff"
            opacity="0.35"
          />
        </pattern>
        <linearGradient id={id("glass-sky")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#dff3ff" />
          <stop offset="1" stopColor="#fff6dc" />
        </linearGradient>
        <linearGradient id={id("bowl")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#f6efe4" />
          <stop offset="1" stopColor="#c9b9a2" />
        </linearGradient>
        <linearGradient id={id("smoothie")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#a6d86a" />
          <stop offset="1" stopColor="#5e9a2c" />
        </linearGradient>
        <linearGradient id={id("board")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#d9a466" />
          <stop offset="1" stopColor="#a8723c" />
        </linearGradient>
        <linearGradient id={id("beam")} x1="0" x2="1" y1="0" y2="0.4">
          <stop offset="0" stopColor="#fff8e0" stopOpacity="0.55" />
          <stop offset="1" stopColor="#fff8e0" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Window({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute top-[30px] left-[4%]"
      width="68"
      height="78"
      viewBox="0 0 68 78"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="0" y="0" width="68" height="78" rx="3" fill="#f7f3ea" />
      <rect
        x="5"
        y="5"
        width="58"
        height="66"
        rx="1.5"
        fill={`url(#${id("glass-sky")})`}
      />
      {/* A tree outside */}
      <circle cx="18" cy="50" r="14" fill="#9fcf8a" opacity="0.75" />
      <circle cx="32" cy="58" r="12" fill="#86bd74" opacity="0.7" />
      <rect x="5" y="62" width="58" height="9" fill="#cfe7b8" opacity="0.8" />
      {/* Muntins */}
      <rect x="33" y="5" width="2.5" height="66" fill="#f7f3ea" />
      <rect x="5" y="36" width="58" height="2.5" fill="#f7f3ea" />
      {/* Sill */}
      <rect x="-2" y="72" width="72" height="6" rx="1.5" fill="#ede4d3" />
      <rect x="-2" y="72" width="72" height="1" fill="#fff" />
    </svg>
  );
}

function Shelf() {
  return (
    <svg
      className="absolute top-[44px] right-[3%]"
      width="96"
      height="50"
      viewBox="0 0 96 50"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="48" cy="46" rx="46" ry="3" fill="#2e4a35" opacity="0.25" />
      {/* Jars */}
      <rect
        x="6"
        y="14"
        width="20"
        height="28"
        rx="4"
        fill="#ffffff"
        opacity="0.55"
      />
      <rect x="8" y="22" width="16" height="18" rx="2.5" fill="#e9d29e" />
      <rect x="5" y="10" width="22" height="6" rx="1.5" fill="#b07a45" />
      <rect
        x="30"
        y="20"
        width="18"
        height="22"
        rx="4"
        fill="#ffffff"
        opacity="0.55"
      />
      <rect x="32" y="27" width="14" height="13" rx="2" fill="#c0562f" />
      <rect x="29" y="16" width="20" height="5.5" rx="1.5" fill="#b07a45" />
      <rect
        x="9"
        y="16"
        width="3"
        height="24"
        rx="1.5"
        fill="#fff"
        opacity="0.6"
      />
      <rect
        x="33"
        y="22"
        width="2.6"
        height="18"
        rx="1.3"
        fill="#fff"
        opacity="0.6"
      />
      {/* Basil */}
      {[
        "M70 26 Q60 12 66 4 Q72 12 70 26 Z",
        "M72 26 Q74 10 84 6 Q84 18 72 26 Z",
        "M71 26 Q58 22 54 14 Q66 14 71 26 Z",
        "M72 26 Q84 24 90 16 Q78 14 72 26 Z",
        "M71 24 Q70 12 74 2 Q78 12 71 24 Z",
      ].map((d) => (
        <path key={d} d={d} fill="#3d8b3a" />
      ))}
      <path
        d="M66 4 Q72 12 70 26 M84 6 Q78 16 72 26"
        stroke="#a8db7f"
        strokeWidth="0.8"
        fill="none"
      />
      <path d="M60 26 H84 L81 42 H63 Z" fill="#c8673e" />
      <rect x="58" y="25" width="28" height="4" rx="1" fill="#de7c50" />
      {/* Plank */}
      <rect x="0" y="42" width="96" height="5" rx="1" fill="#b98250" />
      <rect x="0" y="42" width="96" height="1" fill="#e9bd8a" />
    </svg>
  );
}

function Smoothie({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute left-[6%]"
      style={{ top: COUNTER - 44 }}
      width="34"
      height="66"
      viewBox="0 0 34 66"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="17" cy="63" rx="14" ry="2.6" fill="#3b2a18" opacity="0.35" />
      {/* Straw */}
      <path d="M20 22 L27 2 L30 3 L23 22 Z" fill="#fff" />
      <path
        d="M24.6 9 L28 10 M25.8 5.6 L29.2 6.6 M23.4 12.4 L26.8 13.4 M22.2 15.8 L25.6 16.8"
        stroke="#ff6f61"
        strokeWidth="1.4"
      />
      {/* Glass + smoothie */}
      <path d="M4 18 H30 L27 62 H7 Z" fill="#ffffff" opacity="0.35" />
      <path
        d="M5.4 24 H28.6 L26.4 60 H7.6 Z"
        fill={`url(#${id("smoothie")})`}
      />
      <ellipse cx="17" cy="24" rx="11.6" ry="2.4" fill="#c8ec94" />
      {/* Mint */}
      <path d="M8 22 Q6 14 12 12 Q14 18 8 22 Z" fill="#3fa34d" />
      <path d="M9 21 Q14 16 18 19 Q14 23 9 21 Z" fill="#2f8a3c" />
      {/* Glass highlight */}
      <rect
        x="7.5"
        y="26"
        width="2.2"
        height="30"
        rx="1.1"
        fill="#fff"
        opacity="0.6"
      />
      <path d="M4 18 H30" stroke="#fff" strokeOpacity="0.9" strokeWidth="1" />
    </svg>
  );
}

function GrainBowl({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute left-[calc(50%-55px)]"
      style={{ top: COUNTER - 30 }}
      width="110"
      height="58"
      viewBox="0 0 110 58"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="55" cy="54" rx="40" ry="4" fill="#3b2a18" opacity="0.4" />
      {/* Bowl */}
      <path
        d="M5 24 Q9 54 55 54 Q101 54 105 24 Z"
        fill={`url(#${id("bowl")})`}
      />
      <path
        d="M10 30 Q14 48 40 51"
        stroke="#fff"
        strokeOpacity="0.7"
        strokeWidth="1.6"
        fill="none"
      />
      <ellipse cx="55" cy="24" rx="50" ry="9" fill="#efe6d8" />
      <ellipse cx="55" cy="24.4" rx="46" ry="7.4" fill="#4c7a2c" />
      {/* Greens mound */}
      <path d="M10 25 Q16 8 36 10 Q50 2 66 8 Q86 6 100 25 Z" fill="#3f8a33" />
      <path
        d="M16 20 Q22 12 30 14 M44 8 Q52 4 60 8 M74 10 Q84 10 90 16"
        stroke="#7cc45c"
        strokeWidth="1.4"
        fill="none"
      />
      {/* Purple cabbage */}
      <path d="M70 22 Q76 12 90 14 Q94 20 88 24 Z" fill="#8a3f8f" />
      <path
        d="M74 20 Q80 15 88 17"
        stroke="#d3a3d6"
        strokeWidth="0.8"
        fill="none"
      />
      {/* Avocado fan */}
      {[0, 1, 2, 3].map((i) => (
        <g
          key={i}
          transform={`translate(${22 + i * 6} ${10 + i * 1.2}) rotate(${-20 + i * 12})`}
        >
          <path d="M0 10 Q2 0 10 -2 Q6 6 4 12 Z" fill="#c8e08a" />
          <path
            d="M0 10 Q2 0 10 -2"
            stroke="#2f5a1a"
            strokeWidth="1.2"
            fill="none"
          />
        </g>
      ))}
      {/* Egg half */}
      <ellipse cx="55" cy="14" rx="9" ry="6.4" fill="#fbfaf5" />
      <circle cx="55" cy="14.4" r="4" fill="#ffb31f" />
      <circle cx="53.8" cy="13.2" r="1.2" fill="#ffe08a" />
      {/* Cherry tomatoes */}
      {[
        [66, 16],
        [72, 20],
        [62, 21],
      ].map(([cx, cy]) => (
        <g key={`${cx}-${cy}`}>
          <circle cx={cx} cy={cy} r="3.6" fill="#e5352a" />
          <circle cx={cx - 1.1} cy={cy - 1.2} r="1" fill="#ffb3a6" />
        </g>
      ))}
      {/* Chickpeas */}
      {[
        [40, 20],
        [44, 22],
        [37, 23],
        [46, 18],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" fill="#e3bd7a" />
      ))}
      {/* Sesame */}
      <path
        d="M50 22 h1.4 M58 21 h1.4 M30 18 h1.4 M80 18 h1.4"
        stroke="#fff"
        strokeWidth="0.8"
      />
    </svg>
  );
}

function CuttingBoard({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute right-[4%]"
      style={{ top: COUNTER - 16 }}
      width="84"
      height="40"
      viewBox="0 0 84 40"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="40" cy="35" rx="38" ry="4" fill="#3b2a18" opacity="0.35" />
      {/* Board */}
      <rect
        x="2"
        y="16"
        width="70"
        height="17"
        rx="8"
        fill={`url(#${id("board")})`}
      />
      <rect x="70" y="20" width="12" height="9" rx="4.5" fill="#b17a42" />
      <circle cx="78" cy="24.5" r="1.8" fill="#6b4422" />
      <path
        d="M8 20 H64 M10 27 H60"
        stroke="#8a5a2a"
        strokeWidth="0.6"
        opacity="0.6"
      />
      <rect
        x="2"
        y="16"
        width="70"
        height="2"
        rx="1"
        fill="#f2c88f"
        opacity="0.8"
      />
      {/* Avocado halves */}
      <g transform="translate(10 2)">
        <path
          d="M12 22 Q0 22 1 12 Q2 2 12 1 Q22 2 23 12 Q24 22 12 22 Z"
          fill="#2b4a12"
        />
        <path
          d="M12 20 Q3 20 3.5 12 Q4 4 12 3 Q20 4 20.5 12 Q21 20 12 20 Z"
          fill="#d6e89a"
        />
        <path
          d="M12 20 Q5 18 5 12"
          stroke="#a9cf62"
          strokeWidth="2.4"
          fill="none"
        />
        <circle cx="12" cy="13" r="5" fill="#7a4a24" />
        <circle cx="10.6" cy="11.6" r="1.4" fill="#b98057" />
      </g>
      <g transform="translate(34 4)">
        <path
          d="M11 20 Q0 20 1 11 Q2 2 11 1 Q20 2 21 11 Q22 20 11 20 Z"
          fill="#2b4a12"
        />
        <path
          d="M11 18 Q3 18 3.5 11 Q4 4 11 3 Q18 4 18.5 11 Q19 18 11 18 Z"
          fill="#d6e89a"
        />
        <ellipse cx="11" cy="12" rx="4.4" ry="4.2" fill="#c1d97e" />
      </g>
      {/* Lemon wedge */}
      <path d="M58 26 A9 9 0 0 1 74 18 Z" fill="#ffd93b" />
      <path d="M60 25 A7 7 0 0 1 72 19 Z" fill="#fff2a6" />
      <path
        d="M66 22 L64 25.4 M66 22 L69 23.6 M66 22 L67.6 19"
        stroke="#ffd93b"
        strokeWidth="0.7"
      />
    </svg>
  );
}

export function NutritionDisplayPreview({
  participants,
}: NutritionDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Nutrition"
      backgroundClassName="bg-[#a9c7ad]"
      participants={participants}
      scene={
        <>
          <style href="nutrition-scene-keyframes" precedence="default">
            {NUTRITION_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          {/* Subway-tile backsplash, in px so tiles never stretch */}
          <svg
            className="absolute inset-x-0 top-0 w-full"
            style={{ height: COUNTER }}
            aria-hidden="true"
            focusable="false"
          >
            <rect width="100%" height="100%" fill={`url(#${id("tiles")})`} />
          </svg>
          {/* Shade the wall toward the corners */}
          <div
            className="absolute inset-x-0 top-0 bg-[radial-gradient(90%_120%_at_20%_40%,transparent_30%,rgba(34,62,40,0.38)_100%)]"
            style={{ height: COUNTER }}
          />

          <Window id={id} />
          <Shelf />

          {/* Oak counter top + sage cabinet front */}
          <div
            className="absolute inset-x-0 bg-[linear-gradient(180deg,#e2b47a_0%,#c99258_100%)]"
            style={{ top: COUNTER, height: 22 }}
          >
            <div className="absolute inset-x-0 top-0 h-px bg-[#7a5230]/40" />
            <div className="absolute inset-x-0 bottom-0 h-[2px] bg-[#fff1d6]/70" />
          </div>
          <div
            className="absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,#3f6a4f_0%,#2c4d39_100%)]"
            style={{ top: COUNTER + 22 }}
          >
            <div className="absolute inset-x-0 top-0 h-[5px] bg-black/25" />
            <div className="absolute top-[14px] bottom-[-8px] left-[3%] w-[44%] rounded-[4px] border border-white/10 border-t-white/20" />
            <div className="absolute top-[14px] right-[3%] bottom-[-8px] w-[44%] rounded-[4px] border border-white/10 border-t-white/20" />
          </div>

          {/* Daylight from the window, across wall and counter */}
          <svg
            className="nutrition-light absolute inset-0 h-full w-full"
            viewBox="0 0 300 220"
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <polygon
              points={`20,30 86,30 260,${COUNTER + 22} 60,${COUNTER + 22}`}
              fill={`url(#${id("beam")})`}
            />
          </svg>

          <Smoothie id={id} />
          <GrainBowl id={id} />
          <CuttingBoard id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_40%_40%,transparent_50%,rgba(20,40,26,0.45)_100%)]" />
      }
    />
  );
}
